import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polygon, useMapEvents } from "react-leaflet";
import L from "leaflet";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Undo2, X, Save } from "lucide-react";

const eventMarker = new L.DivIcon({
  className: "",
  html: '<div style="width:22px;height:22px;background:#EF4444;border:3px solid #fff;border-radius:50%"></div>',
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function ClickToAdd({ onAdd }) {
  useMapEvents({ click: (e) => onAdd([e.latlng.lat, e.latlng.lng]) });
  return null;
}

export default function EventAreaEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [ev, setEv] = useState(null);
  const [points, setPoints] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/events/${id}`);
        setEv(data);
        const coords = ((data.geometry?.coordinates) || [[]])[0];
        // trim closing point
        const ring = coords.slice(0, -1).length ? coords.slice(0, -1) : coords;
        setPoints(ring.map(([lng, lat]) => [lat, lng]));
      } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    })();
  }, [id]);

  const canManage = user && (user.role === "admin" || user.role === "moderator" || user.verified_entity);

  const submit = async () => {
    if (points.length < 3) return toast.error("Servono almeno 3 punti");
    setSaving(true);
    try {
      const ring = points.map(([lat, lng]) => [lng, lat]);
      ring.push(ring[0]);
      await api.post(`/events/${id}/area`, { geometry: { type: "Polygon", coordinates: [ring] }, note: note.trim() || null });
      toast.success("Area aggiornata");
      nav(`/events/${id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  if (!ev) return <div className="min-h-screen bg-[#F7F5F0]"><AppHeader /><div className="p-8 text-slate-500">Caricamento…</div></div>;
  if (!canManage) return <div className="min-h-screen bg-[#F7F5F0]"><AppHeader /><div className="p-8 text-slate-700">{t("not_authorized_dot")}</div></div>;

  const center = ev.centroid ? [ev.centroid.lat, ev.centroid.lng] : [42, 12];

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="event-area-edit-page">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-6 pb-4"><BackButton /></div>
      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-red-700 font-semibold mb-1">Evento straordinario</div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-3">Modifica area</h1>
        <p className="text-sm text-slate-600 mb-4">Ridisegna il poligono cliccando i vertici sulla mappa. La geometria precedente rimane consultabile nello storico area.</p>

        <div className="rounded-2xl overflow-hidden border-2 border-slate-200 h-[500px] mb-3" data-testid="area-edit-map">
          <MapContainer center={center} zoom={11} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
            <ClickToAdd onAdd={(p) => setPoints((prev) => [...prev, p])} />
            {points.map((p, i) => <Marker key={i} position={p} icon={eventMarker} />)}
            {points.length >= 3 && <Polygon positions={points} pathOptions={{ color: "#EF4444", weight: 2, fillColor: "#EF4444", fillOpacity: 0.18 }} />}
          </MapContainer>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button size="sm" variant="outline" onClick={() => setPoints((p) => p.slice(0, -1))} disabled={!points.length} className="rounded-full" data-testid="area-edit-undo"><Undo2 size={12} className="mr-1" /> Annulla ultimo</Button>
          <Button size="sm" variant="outline" onClick={() => setPoints([])} disabled={!points.length} className="rounded-full" data-testid="area-edit-clear"><X size={12} className="mr-1" /> Pulisci</Button>
          <div className="text-xs text-slate-500 ml-auto self-center" data-testid="area-edit-count">{points.length} punti</div>
        </div>
        <Textarea data-testid="area-edit-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota facoltativa (verrà mostrata nello storico e nella notifica ai follower)" rows={2} className="rounded-xl mb-3" />
        <Button data-testid="area-edit-submit" onClick={submit} disabled={saving || points.length < 3} className="rounded-full bg-red-600 hover:bg-red-700 text-white hover:text-white h-11 px-6">
          {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : <Save className="mr-2" size={14} />} Salva nuova area
        </Button>
      </div>
    </div>
  );
}
